/**
 * The body of an authored document, as a surface should render it.
 *
 * The chat commonly opens a document with an `h1` block carrying the same text it
 * put in the document's title, so every surface that renders a header AND the body
 * shows the title twice (observed on a real dev document, MYAC-1162). The renderer
 * is not the culprit — `render_blocks` emits the blocks and nothing else — so the
 * duplicate is authored content, and stripping it here repairs the documents that
 * already exist rather than only the ones written after a prompt change.
 *
 * The repeat is not always verbatim: the chat also opens with a SHORTENED title
 * ("… Diagnostic et Priorisation" under the title "… Diagnostic et Priorisation de
 * la Conformité", MYAC-1448), which an exact match let through.
 *
 * Deliberately narrow: it removes a LEADING `h1` and only when it says nothing the
 * title does not already say. A first heading that differs is the document's own
 * structure and is left alone.
 */

/** ATX only — `render_blocks` emits `# text`, never the setext `text\n===` form. */
const ATX_H1 = /^#[^\S\n]+(.*?)[^\S\n]*#*[^\S\n]*$/

/** Any ATX heading, h1 to h6, up to three spaces of indent as CommonMark allows. */
const ATX_HEADING = /^( {0,3})(#{1,6})(?=[^\S\n]|$)/

/** Opening or closing line of a fenced code block — a `#` in there is not a heading. */
const FENCE = /^ {0,3}(`{3,}|~{3,})/

function normalize(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLocaleLowerCase()
}

/**
 * Whether `heading` only repeats `title`: the same words, or the title's opening
 * words cut short. The half-the-title floor keeps a generic opener ("Summary" under
 * "Summary of supplier audits") from reading as a repeat — at that length it is
 * more likely a section heading than a truncated title.
 */
function repeatsTitle(heading: string, title: string): boolean {
  if (heading === title) return true
  const headingWords = heading.split(' ')
  const titleWords = title.split(' ')
  if (headingWords.length * 2 < titleWords.length) return false
  return headingWords.every((word, i) => word === titleWords[i])
}

/**
 * `markdown` without its opening title heading, when that heading duplicates
 * `title`. Returns the input unchanged in every other case — including an empty
 * title, which must never license stripping a real heading.
 */
export function stripDuplicateTitleHeading(markdown: string, title: string): string {
  const wanted = normalize(title ?? '')
  if (!wanted || !markdown) return markdown

  const lines = markdown.split('\n')
  let start = 0
  while (start < lines.length && lines[start].trim() === '') start += 1
  if (start >= lines.length) return markdown

  const heading = ATX_H1.exec(lines[start].trim())
  if (!heading || !repeatsTitle(normalize(heading[1]), wanted)) return markdown

  let next = start + 1
  while (next < lines.length && lines[next].trim() === '') next += 1
  return lines.slice(next).join('\n')
}

/**
 * `markdown` with every heading one level down, when it contains an `h1`. The
 * surface's header owns the page's only `h1`; a body `h1` would be a second one,
 * which is an accessibility fault as well as a visual one. Shifting every level —
 * not only the `h1`s — keeps the body's own hierarchy intact. An `h6` stays an
 * `h6`: there is no level below it.
 */
export function demoteHeadingsBelowTitle(markdown: string): string {
  if (!markdown) return markdown

  const lines = markdown.split('\n')
  const isHeading: boolean[] = []
  let fence: string | null = null
  let hasH1 = false
  for (const line of lines) {
    const marker = FENCE.exec(line)?.[1]
    if (marker && (fence === null || (marker[0] === fence[0] && marker.length >= fence.length))) {
      fence = fence === null ? marker : null
      isHeading.push(false)
      continue
    }
    const heading = fence === null ? ATX_HEADING.exec(line) : null
    isHeading.push(heading !== null)
    if (heading?.[2] === '#') hasH1 = true
  }
  if (!hasH1) return markdown

  return lines
    .map((line, i) =>
      isHeading[i]
        ? line.replace(ATX_HEADING, (all, indent, hashes) =>
            hashes.length < 6 ? `${indent}#${hashes}` : all,
          )
        : line,
    )
    .join('\n')
}

/**
 * The body as a surface with its own title header renders it: the repeated title
 * gone, and no heading left at the header's level.
 *
 * For DISPLAY only. The `.md` download keeps the raw body: a standalone file has no
 * header chrome, so its `h1` is the only title it has.
 */
export function documentBodyForDisplay(markdown: string, title: string): string {
  return demoteHeadingsBelowTitle(stripDuplicateTitleHeading(markdown, title))
}
